import * as React from "react";
import { ReactNode } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import { palette } from "./palette";

export const customTypographyOptions = {
  h1: {
    fontSize: 30,
    fontWeight: 400,
    color: palette.black,
    lineHeight: "34px",
  },
  stepperTitle: {
    fontSize: 16,
    fontWeight: 400,
    color: palette.black,
    lineHeight: "16px",
    letterSpacing: "0em",
  },
  h3: {
    color: palette.black,
    fontSize: 20,
    fontWeight: 700,
    lineHeight: "23px",
    letterSpacing: "0em",
  },
  h4: {
    fontSize: 18,
    fontWeight: 400,
    color: palette.black,
    lineHeight: "21px",
    letterSpacing: "0em",
  },
  h5: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: "21px",
    letterSpacing: "0em",
  },
  h5sb: {
    fontSize: 18,
    fontWeight: 600,
    color: palette.black,
  },
  h5md: {
    fontSize: 20,
    fontWeight: 400,
    color: palette.black,
    lineHeight: "23px",
  },
  summaryEmptyDescription: {
    fontSize: 20,
    fontWeight: 400,
    lineHeight: "29px",
    letterSpacing: "0em",
  },
  inputTitle: {
    fontSize: 18,
    fontWeight: 400,
    lineHeight: "21px",
    letterSpacing: "0em",
  },
  fieldText: {
    fontSize: 16,
    fontWeight: 400,
    color: palette.black,
    lineHeight: "18.4px",
  },
  secondaryText: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "16px",
    letterSpacing: "0em",
    color: palette.black,
  },
  labelErrorText: {
    color: palette.error,
    fontSize: 12,
    fontWeight: 400,
  },
  cardLabelText: {
    color: palette.black,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: "16px",
    letterSpacing: "0em",
  },
  cardSeatsText: {
    color: palette.black,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: "15px",
    letterSpacing: "0em",
  },
  cardTripTypeText: {
    color: palette.black,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: "15px",
    letterSpacing: "0em",
  },
  cardFooter: {
    color: palette.black,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: "18px",
    letterSpacing: "0em",
  },
};

export const customTypographyOptionsMobile = {
  h1: {
    fontSize: 20,
    fontWeight: 400,
    color: palette.black,
    lineHeight: "23px",
  },
  stepperTitle: {
    fontSize: 16,
    fontWeight: 400,
    color: palette.black,
    lineHeight: "16px",
    letterSpacing: "0em",
  },
  h3: {
    color: palette.black,
    fontSize: 15,
    fontWeight: 700,
    lineHeight: "17.25px",
    letterSpacing: "0em",
  },
  h4: {
    fontSize: 18,
    fontWeight: 400,
    color: palette.black,
    lineHeight: "21px",
    letterSpacing: "0em",
  },
  h5: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: "21px",
    letterSpacing: "0em",
  },
  h5sb: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: "16.1px",
    color: palette.black,
  },
  h5md: {
    fontSize: 15,
    fontWeight: 400,
    color: palette.black,
    lineHeight: "17.25px",
  },
  summaryEmptyDescription: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "16.1px",
    letterSpacing: "0em",
  },
  inputTitle: {
    fontSize: 18,
    fontWeight: 400,
    lineHeight: "20.7px",
    letterSpacing: "0em",
  },
  fieldText: {
    fontSize: 16,
    fontWeight: 400,
    color: palette.black,
    lineHeight: "18.4px",
  },
  secondaryText: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "16px",
    letterSpacing: "0em",
    color: palette.black,
  },
  labelErrorText: {
    color: palette.error,
    fontSize: 12,
    fontWeight: 400,
  },
  cardLabelText: {
    color: palette.black,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: "16px",
    letterSpacing: "0em",
  },
  cardSeatsText: {
    color: palette.black,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: "15px",
    letterSpacing: "0em",
  },
  cardTripTypeText: {
    color: palette.black,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: "15px",
    letterSpacing: "0em",
  },
  cardFooter: {
    color: palette.black,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: "18px",
    letterSpacing: "0em",
  },
};

export const createCustomTheme = (isMobile: boolean) => {
  return createTheme({
    typography: !isMobile
      ? customTypographyOptions
      : customTypographyOptionsMobile,
    breakpoints: {
      values: {
        xs: 0,
        sm: 769,
        md: 1025,
        lg: 1350,
        xl: 1536,
      },
    },
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            border: `1px solid ${palette.grey}`,
            "& fieldset": {
              border: "none",
            },
            "&:hover": {
              "& fieldset": {
                // border: 'none'
              },
            },
            "&.Mui-focused": {
              border: `1px solid ${palette.accent}`,
            },
            "&.Mui-disabled ": {
              backgroundColor: palette.lightGrey,
            },
            "&.error-Border": {
              border: `1px solid ${palette.error}`,
            },
            "&.Mui-error": {
              border: `1px solid ${palette.error}`,
            },
            minHeight: 60,
            backgroundColor: palette.white,
            display: "flex",
            borderRadius: 10,
            paddingLeft: 8,
          },
        },
      },
      MuiButton: {
        variants: [
          {
            // TODO type
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            props: { variant: "addonButtons" as any },
            style: {
              "&:hover": {
                backgroundColor: "inherit",
              },
              padding: "0",
              height: "24px",
            },
          },
          {
            // TODO type
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            props: { variant: "buttonEffectsWrapper" as any },
            style: {
              "&:hover": {
                backgroundColor: "inherit",
              },
              padding: "0",
              height: "auto",
              width: "auto",
              border: "none",
            },
          },
        ],
        styleOverrides: {
          root: {
            minWidth: "25px",
            height: "25px",
            borderRadius: "5px",
            border: `1px solid ${palette.accent}`,
            color: palette.accent,
            textTransform: "none",
            "&:disabled": {
              border: `1px solid ${palette.grey}`,
              color: palette.grey,
            },
            "&:hover": {
              backgroundColor: palette.accent,
            },
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: palette.accent,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          root: {
            ".MuiDrawer-paper": {
              backgroundColor: "#ffffff00",
              boxShadow: "none",
            },
          },
        },
      },
      MuiFormControlLabel: {
        variants: [
          {
            // TODO type
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            props: { variant: "paymentButtons" } as any,
            style: {
              "&.activeBorder": {
                border: "1px solid " + palette.accent,
                backgroundColor: palette.accent + "09",
              },

              "& .MuiFormControlLabel-label": {
                display: "flex",
              },
              "& .Mui-checked": {
                "& svg": {
                  color: palette.accent,
                },
              },
              "& svg": {
                color: palette.grey,
              },
              marginRight: "0 !important",
              border: "1px solid " + palette.grey,
              borderRadius: 10,
              padding: 10,
              marginLeft: 0,
            },
          },
        ],
      },
    },
  });
};

const Theme = ({
  children,
  isMobile,
}: {
  children: ReactNode;
  isMobile: boolean;
}) => {
  const theme = createCustomTheme(isMobile);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default Theme;
